import { AnalyticsEvent, toMetaEvent } from "@/lib/analytics/events";
import type {
  AnalyticsProperties,
  ClientAnalyticsAdapter,
} from "@/lib/analytics/types";

declare global {
  interface Window {
    fbq?: (
      command: string,
      ...args: unknown[]
    ) => void;
    _fbq?: unknown;
  }
}

let pixelInited = false;
let pendingEmail: string | null = null;

function pixelId(): string {
  return process.env.NEXT_PUBLIC_FB_PIXEL_ID?.trim() ?? "";
}

export const facebookClientAdapter: ClientAnalyticsAdapter = {
  name: "facebook",

  init() {
    const id = pixelId();
    if (!id || typeof window === "undefined") return;
    if (typeof window.fbq !== "function") return;
    // Meta warns on every extra init with the same Pixel ID.
    if (pixelInited) return;

    if (pendingEmail) {
      window.fbq("init", id, { em: pendingEmail });
    } else {
      window.fbq("init", id);
    }
    pixelInited = true;
  },

  track(name, properties, eventId) {
    if (!pixelId() || typeof window === "undefined" || typeof window.fbq !== "function") {
      return;
    }
    if (!pixelInited) {
      facebookClientAdapter.init();
    }
    /* istanbul ignore next -- `init()` above always flips the flag when `fbq` exists */
    if (!pixelInited) return;

    const mapped = toMetaEvent(name);
    const options = { eventID: eventId };
    const params = toMetaParams(name, properties);

    if (mapped.kind === "standard") {
      window.fbq("track", mapped.name, params, options);
    } else {
      window.fbq("trackCustom", mapped.name, params, options);
    }
  },

  identify(_userId, traits) {
    const email = traits?.email?.trim();
    if (email) pendingEmail = email.toLowerCase();

    // Do not call init again after first init — Meta logs "Duplicate Pixel ID".
    // Advanced Matching email is applied on the first init; later matches go via CAPI.
    if (!pixelInited) {
      facebookClientAdapter.init();
    }
  },

  reset() {
    pendingEmail = null;
    // Pixel cookie identity is not cleared by reset.
  },
};

function toMetaParams(
  name: string,
  properties: AnalyticsProperties | undefined,
): Record<string, unknown> {
  if (!properties) return {};
  const params: Record<string, unknown> = { ...properties };

  // Meta Purchase / InitiateCheckout prefer value + currency.
  if (typeof properties.value === "number") params.value = properties.value;
  if (typeof properties.currency === "string") params.currency = properties.currency;

  if (
    name === AnalyticsEvent.Purchase ||
    name === AnalyticsEvent.InitiateCheckout
  ) {
    if (properties.product_type != null) {
      params.content_category = properties.product_type;
    }
  }

  return params;
}
